import {
  atlasLogoMicrosoftTeamsColor,
  atlasPhone,
  serviceCoderpad,
  serviceCodesignal,
  serviceGoogleMeet,
  serviceZoom,
} from "@resource/atlas/icons";
import {
  ConferenceServiceEnum,
  conferenceUrlType,
  displayPhoneNumber,
  isPossibleNumber,
} from "@resource/common";
import { useMemo } from "react";

export type ServiceIconType = {
  data: JSX.Element;
  size: number;
};

export type ExternalServiceLabelAndIcon = {
  icon: ServiceIconType | string | null;
  label?: string | null;
  textColorClassname: string | null;
};

export function getExternalServiceLabelAndIcon({
  url,
  isInPast,
}: {
  url?: string | null;
  isInPast?: boolean;
}) {
  const data: ExternalServiceLabelAndIcon = {
    icon: null,
    label: null,
    textColorClassname: null,
  };

  if (!url) {
    return data;
  }

  const type = conferenceUrlType(url ?? "");
  const pastTextColorClassname = "!text-subtle";
  switch (type) {
    case ConferenceServiceEnum.Zoom:
      data.label = "Zoom meeting";
      data.icon = serviceZoom;
      data.textColorClassname = isInPast
        ? pastTextColorClassname
        : "!text-[#008BFF]";
      break;
    case ConferenceServiceEnum.Meet:
      data.label = "Google meet";
      data.icon = serviceGoogleMeet;
      data.textColorClassname = isInPast
        ? pastTextColorClassname
        : "!text-[#1A73E8]";
      break;
    case ConferenceServiceEnum.Teams:
      data.label = "Teams meeting";
      data.icon = atlasLogoMicrosoftTeamsColor;
      data.textColorClassname = isInPast
        ? pastTextColorClassname
        : "!text-[#5C6BC0]";
      break;
    case ConferenceServiceEnum.Codesignal:
      data.label = "Codesignal link";
      data.icon = serviceCodesignal;
      data.textColorClassname = isInPast
        ? pastTextColorClassname
        : "!text-[#004AE1]";
      break;
    case ConferenceServiceEnum.Coderpad:
      data.label = "Coderpad link";
      data.icon = serviceCoderpad;
      data.textColorClassname = isInPast
        ? pastTextColorClassname
        : "!text-[#CC4E41]";
      break;
    case ConferenceServiceEnum.Phone:
      data.icon = atlasPhone;
      data.textColorClassname = isInPast
        ? pastTextColorClassname
        : "!text-dark";
      break;
    default:
      break;
  }
  return data;
}

export function useExternalServiceLabelAndIcon({
  url,
  isInPast,
}: {
  url?: string | null;
  isInPast?: boolean;
}): ExternalServiceLabelAndIcon {
  const externalServiceLabelAndIcon = useMemo(
    () =>
      getExternalServiceLabelAndIcon({
        url,
        isInPast,
      }),
    [url, isInPast]
  );

  return externalServiceLabelAndIcon;
}

export function getDisplayInfo({
  url,
  label,
}: {
  url: string;
  label?: string | null;
}): {
  displayLabel: string;
  href: string;
  displayUrl?: string;
} {
  const isPhone = isPossibleNumber(url);

  if (isPhone) {
    return {
      displayLabel: displayPhoneNumber(url),
      href: `tel:${url}`,
    };
  }

  return {
    displayLabel: label ?? url ?? "",
    href: url,
  };
}
